import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Baby, FileText, User, Calendar, Loader2, ArrowRight, Command } from 'lucide-react'
import { minoriApi, entriesApi, usersApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

/**
 * GlobalSearch - Componente ricerca globale con Command Palette style
 * Accessibile con Cmd/Ctrl + K
 */
export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState({ minori: [], entries: [], users: [] })
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const { isCoordinatore } = useAuth()

  // Focus input quando aperto
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
      setQuery('')
      setResults({ minori: [], entries: [], users: [] })
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Keyboard shortcut globale
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K per aprire
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isOpen) {
          onClose()
        }
      }
      // Escape per chiudere
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults({ minori: [], entries: [], users: [] })
      return
    }

    const timer = setTimeout(() => {
      performSearch(query)
    }, 300)

    return () => clearTimeout(timer)
  }, [query])

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return
    
    setLoading(true)
    try {
      const [minoriRes, entriesRes, usersRes] = await Promise.all([
        minoriApi.list({ search: searchQuery, limit: 5 }).catch(() => ({ data: [] })),
        entriesApi.list({ search: searchQuery, limit: 5 }).catch(() => ({ data: [] })),
        isCoordinatore() 
          ? usersApi.list({ search: searchQuery, limit: 5 }).catch(() => ({ data: [] }))
          : { data: [] }
      ])

      setResults({
        minori: Array.isArray(minoriRes.data) ? minoriRes.data : minoriRes.data?.data || [],
        entries: Array.isArray(entriesRes.data) ? entriesRes.data : entriesRes.data?.data || [],
        users: Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || []
      })
      setSelectedIndex(0)
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setLoading(false)
    }
  }

  // Flatten results per navigazione keyboard
  const flatResults = [
    ...results.minori.map(m => ({ type: 'minore', data: m })),
    ...results.entries.map(e => ({ type: 'entry', data: e })),
    ...results.users.map(u => ({ type: 'user', data: u }))
  ]

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault()
      handleSelect(flatResults[selectedIndex])
    }
  }

  const handleSelect = (item) => {
    onClose()
    switch (item.type) {
      case 'minore':
        navigate(`/minori/${item.data._id}`)
        break
      case 'entry':
        navigate(`/entries/${item.data._id}/edit`)
        break
      case 'user':
        navigate(`/users`)
        break
    }
  }

  if (!isOpen) return null

  const hasResults = flatResults.length > 0
  const showQuickActions = !query.trim()

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div 
        className="relative min-h-screen flex items-start justify-center pt-[15vh] px-4"
        onClick={onClose}
      >
        <div 
          className="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Cerca minori, registrazioni, utenti..."
              className="flex-1 px-4 py-4 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400"
            />
            {loading && <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />}
            {query && !loading && (
              <button onClick={() => setQuery('')} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Quick Actions (quando vuoto) */}
          {showQuickActions && (
            <div className="p-4">
              <p className="text-xs font-medium text-gray-400 uppercase mb-3">Azioni Rapide</p>
              <div className="space-y-1">
                <QuickAction
                  icon={Baby}
                  label="Vai a Minori"
                  shortcut="⌘M"
                  onClick={() => { onClose(); navigate('/minori'); }}
                />
                <QuickAction
                  icon={FileText}
                  label="Nuova Registrazione"
                  shortcut="⌘N"
                  onClick={() => { onClose(); navigate('/entries/new'); }}
                />
                <QuickAction
                  icon={Calendar}
                  label="Registrazioni di Oggi"
                  shortcut="⌘T"
                  onClick={() => { 
                    onClose(); 
                    const today = new Date().toISOString().split('T')[0];
                    navigate(`/entries?dataInizio=${today}&dataFine=${today}`); 
                  }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {!showQuickActions && (
            <div className="max-h-96 overflow-y-auto">
              {/* Minori */}
              {results.minori.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Minori</p>
                  {results.minori.map((minore, idx) => (
                    <ResultItem
                      key={minore._id}
                      icon={Baby}
                      title={minore.nomeCompleto}
                      subtitle={minore.educatoreReferente?.nome ? `Ref: ${minore.educatoreReferente.nome}` : null}
                      isSelected={flatResults[selectedIndex]?.data?._id === minore._id}
                      onClick={() => handleSelect({ type: 'minore', data: minore })}
                    />
                  ))}
                </div>
              )}

              {/* Entries */}
              {results.entries.length > 0 && (
                <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Registrazioni</p>
                  {results.entries.map((entry, idx) => (
                    <ResultItem
                      key={entry._id}
                      icon={FileText}
                      title={entry.minore?.nomeCompleto || 'Registrazione'}
                      subtitle={`${new Date(entry.data).toLocaleDateString('it-IT')} - ${entry.educatore}`}
                      isSelected={flatResults[selectedIndex]?.data?._id === entry._id}
                      onClick={() => handleSelect({ type: 'entry', data: entry })}
                    />
                  ))}
                </div>
              )}

              {/* Users */}
              {results.users.length > 0 && (
                <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">Utenti</p>
                  {results.users.map((user, idx) => (
                    <ResultItem
                      key={user._id}
                      icon={User}
                      title={`${user.nome} ${user.cognome}`}
                      subtitle={user.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'}
                      isSelected={flatResults[selectedIndex]?.data?._id === user._id}
                      onClick={() => handleSelect({ type: 'user', data: user })}
                    />
                  ))}
                </div>
              )}

              {/* No results */}
              {!hasResults && query.trim() && !loading && (
                <div className="p-8 text-center">
                  <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Nessun risultato per "{query}"</p>
                  <p className="text-sm text-gray-400 mt-1">Prova con un termine diverso</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">↑↓</kbd>
                Naviga
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">↵</kbd>
                Seleziona
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">esc</kbd>
                Chiudi
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Quick Action Item
function QuickAction({ icon: Icon, label, shortcut, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-900 transition-colors">
          <Icon className="h-4 w-4 text-gray-500 group-hover:text-primary-600" />
        </div>
        <span className="text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <kbd className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-400">
        {shortcut}
      </kbd>
    </button>
  )
}

// Result Item
function ResultItem({ icon: Icon, title, subtitle, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
        isSelected 
          ? 'bg-primary-50 dark:bg-primary-900/30' 
          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
        <div className="text-left">
          <p className={`font-medium ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
            {title}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
      <ArrowRight className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-gray-300'}`} />
    </button>
  )
}

// Hook per attivare GlobalSearch
export function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev)
  }
}
